import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Users, UserCheck, UserX, TrendingUp,
  Download, Search, Trash2, RefreshCw, Mail, Send, X, Upload,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABELS = {
  footer_newsletter: 'Footer Newsletter',
  learn_gate: 'Learn Gate',
  home_hero: 'Home Hero',
  post_inline: 'Inline (Post)',
  admin_seed: 'Admin Seed',
  unknown: 'Unknown',
};

const SOURCE_COLORS = {
  footer_newsletter: 'bg-emerald-100 text-emerald-800',
  learn_gate: 'bg-indigo-100 text-indigo-800',
  home_hero: 'bg-amber-100 text-amber-800',
  post_inline: 'bg-blue-100 text-blue-800',
  admin_seed: 'bg-gray-100 text-gray-800',
  unknown: 'bg-gray-100 text-gray-600',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const StatCard = ({ icon: Icon, label, value, hint, testid, accent = 'gray' }) => {
  const accents = {
    gray: 'bg-gray-50 text-gray-700',
    green: 'bg-emerald-50 text-emerald-700',
    red: 'bg-red-50 text-red-700',
    blue: 'bg-blue-50 text-blue-700',
    indigo: 'bg-indigo-50 text-indigo-700',
  };
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-gray-500">{label}</span>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${accents[accent]}`}>
          <Icon size={18} />
        </div>
      </div>
      <div className="text-3xl font-light text-gray-900">{value}</div>
      {hint ? <div className="text-xs text-gray-500 mt-1">{hint}</div> : null}
    </div>
  );
};

const GrowthSparkline = ({ data }) => {
  // data: [{date, count}] (30 entries)
  const max = Math.max(1, ...data.map(d => d.count));
  return (
    <div className="flex items-end gap-1 h-24" data-testid="growth-sparkline">
      {data.map((d) => {
        const h = Math.max(2, Math.round((d.count / max) * 92));
        return (
          <div
            key={d.date}
            className="flex-1 bg-black/80 hover:bg-black rounded-t-sm transition-colors"
            style={{ height: `${h}px` }}
            title={`${d.date}: ${d.count} new`}
          />
        );
      })}
    </div>
  );
};

const SourceBars = ({ breakdown, totalActive }) => {
  if (!breakdown.length) {
    return <p className="text-sm text-gray-500">No source data yet.</p>;
  }
  return (
    <div className="space-y-3" data-testid="source-breakdown">
      {breakdown.map((b) => {
        const pct = totalActive ? Math.round((b.count / totalActive) * 100) : 0;
        return (
          <div key={b.source}>
            <div className="flex justify-between text-sm mb-1">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${SOURCE_COLORS[b.source] || SOURCE_COLORS.unknown}`}>
                {SOURCE_LABELS[b.source] || b.source}
              </span>
              <span className="text-gray-600">{b.count} <span className="text-gray-400">· {pct}%</span></span>
            </div>
            <div className="h-2 bg-gray-100 rounded overflow-hidden">
              <div className="h-full bg-black/80" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AdminSubscribers = () => {
  const [analytics, setAnalytics] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('');
  const [search, setSearch] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [deletingEmail, setDeletingEmail] = useState('');
  // Bulk email
  const [emailPanelOpen, setEmailPanelOpen] = useState(false);
  const [emailKind, setEmailKind] = useState('post'); // post | lesson | magnet
  const [emailOptions, setEmailOptions] = useState({ posts: [], lessons: [], magnets: [] });
  const [selectedSlug, setSelectedSlug] = useState('');
  const [selectedEmails, setSelectedEmails] = useState(new Set()); // emails checked from the table
  const [extraEmails, setExtraEmails] = useState(''); // comma-separated free-form emails
  const [sendToAll, setSendToAll] = useState(false);
  const [subjectOverride, setSubjectOverride] = useState('');
  const [isSending, setIsSending] = useState(false);
  // Bulk import
  const [importPanelOpen, setImportPanelOpen] = useState(false);
  const [importEmails, setImportEmails] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const navigate = useNavigate();

  const headers = useCallback(() => {
    const t = localStorage.getItem('admin_token');
    if (!t) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${t}` };
  }, [navigate]);

  const loadAll = useCallback(async () => {
    const h = headers();
    if (!h) return;
    setRefreshing(true);
    try {
      const params = { status: statusFilter, limit: 500 };
      if (sourceFilter) params.source = sourceFilter;
      if (search.trim()) params.search = search.trim();
      const [a, l] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/subscribers/analytics`, { headers: h }),
        axios.get(`${BACKEND_URL}/api/admin/subscribers`, { headers: h, params }),
      ]);
      setAnalytics(a.data);
      setItems(l.data.items || []);
      setTotal(l.data.total || 0);
    } catch (e) {
      if (e.response?.status === 401) navigate('/admin/login');
      else toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [headers, navigate, statusFilter, sourceFilter, search]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Load email-broadcast options once
  useEffect(() => {
    const h = headers();
    if (!h) return;
    axios
      .get(`${BACKEND_URL}/api/admin/email/options`, { headers: h })
      .then((res) => setEmailOptions(res.data || { posts: [], lessons: [], magnets: [] }))
      .catch(() => {});
  }, [headers]);

  // Default the selected slug to the first item whenever kind or options change
  useEffect(() => {
    const list =
      emailKind === 'post' ? emailOptions.posts :
      emailKind === 'lesson' ? emailOptions.lessons :
      emailOptions.magnets;
    if (list && list.length > 0) {
      setSelectedSlug((prev) => (list.find(x => x.slug === prev) ? prev : list[0].slug));
    } else {
      setSelectedSlug('');
    }
  }, [emailKind, emailOptions]);

  const toggleEmailSelected = (email) => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setSelectedEmails((prev) => {
      const allActive = items.filter(s => s.active).map(s => s.email);
      const everyChecked = allActive.length > 0 && allActive.every(e => prev.has(e));
      if (everyChecked) {
        const next = new Set(prev);
        allActive.forEach(e => next.delete(e));
        return next;
      }
      return new Set([...prev, ...allActive]);
    });
  };

  const parseExtraEmails = (text) => {
    return Array.from(new Set(
      text
        .split(/[\s,;]+/)
        .map(e => e.trim().toLowerCase())
        .filter(e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))
    ));
  };

  const handleBulkImport = async () => {
    const h = headers();
    if (!h) return;
    if (!importEmails.trim()) { toast.error('Paste at least one email'); return; }
    setIsImporting(true);
    setImportResult(null);
    const toastId = toast.loading('Importing subscribers…');
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/subscribers/bulk-import`,
        { emails: importEmails, source: 'admin_bulk_import', reactivate: true },
        { headers: h, timeout: 60000 }
      );
      setImportResult(res.data);
      const { added, reactivated, already_active, invalid_count } = res.data;
      toast.success(`Added ${added} · Reactivated ${reactivated} · Already active ${already_active}${invalid_count ? ` · Invalid ${invalid_count}` : ''}`, { id: toastId, duration: 8000 });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed', { id: toastId });
    } finally {
      setIsImporting(false);
    }
  };

  const handleSendBroadcast = async () => {
    const h = headers();
    if (!h) return;
    if (!selectedSlug) { toast.error('Pick an item to send'); return; }

    let recipients = [];
    if (!sendToAll) {
      recipients = [
        ...selectedEmails,
        ...parseExtraEmails(extraEmails),
      ];
      // Dedup
      recipients = Array.from(new Set(recipients.map(e => e.toLowerCase())));
      if (recipients.length === 0) {
        toast.error('Pick subscribers, type emails, or check "Send to all active"');
        return;
      }
    }

    const total = sendToAll ? (analytics?.active || 0) : recipients.length;
    if (!window.confirm(`Send this email to ${total} recipient${total === 1 ? '' : 's'}?`)) return;

    setIsSending(true);
    const toastId = toast.loading(`Sending to ${total}…`);
    try {
      const body = {
        kind: emailKind,
        slug: selectedSlug,
        send_to_all: sendToAll,
        recipients: sendToAll ? null : recipients,
      };
      if (subjectOverride.trim()) body.subject_override = subjectOverride.trim();
      const res = await axios.post(`${BACKEND_URL}/api/admin/email/broadcast`, body, { headers: h, timeout: 300000 });
      const { sent, failed, recipients: total_recipients } = res.data;
      toast.success(`Sent ${sent}/${total_recipients}${failed ? ` · ${failed} failed` : ''}`, { id: toastId, duration: 8000 });
      // Reset selection
      setSelectedEmails(new Set());
      setExtraEmails('');
      setSendToAll(false);
      setSubjectOverride('');
      setEmailPanelOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed', { id: toastId });
    } finally {
      setIsSending(false);
    }
  };

  const handleExport = async () => {
    const h = headers();
    if (!h) return;
    setDownloading(true);
    try {
      const params = { status: statusFilter };
      if (sourceFilter) params.source = sourceFilter;
      const res = await axios.get(`${BACKEND_URL}/api/admin/subscribers/export.csv`, {
        headers: h, params, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch {
      toast.error('Export failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Permanently delete ${email}? This cannot be undone.`)) return;
    const h = headers();
    if (!h) return;
    setDeletingEmail(email);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/subscribers/${encodeURIComponent(email)}`, { headers: h });
      toast.success('Subscriber removed');
      loadAll();
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeletingEmail('');
    }
  };

  const sourceOptions = useMemo(() => {
    if (!analytics) return [];
    return analytics.source_breakdown.map(s => s.source);
  }, [analytics]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="subscribers-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/admin/dashboard" className="flex items-center gap-2 text-gray-600 hover:text-black" data-testid="back-to-dashboard">
            <ArrowLeft size={20} /><span>Back to Dashboard</span>
          </Link>
          <h1 className="text-2xl font-light">Subscribers</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setImportPanelOpen((v) => !v)}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
              data-testid="open-import-panel-btn"
            >
              <Upload size={14} /> <span>Import</span>
            </button>
            <button
              onClick={() => setEmailPanelOpen((v) => !v)}
              className="flex items-center gap-2 px-3 py-2 bg-black text-white rounded-md hover:bg-gray-800 text-sm"
              data-testid="open-email-panel-btn"
            >
              <Mail size={14} /> <span>Send email</span>
            </button>
            <button
              onClick={loadAll}
              disabled={refreshing}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-black"
              data-testid="subscribers-refresh-btn"
            >
              {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Stat row */}
        <section className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard testid="stat-total" icon={Users} label="Total subscribers" value={analytics?.total ?? 0} accent="gray" />
          <StatCard testid="stat-active" icon={UserCheck} label="Active" value={analytics?.active ?? 0} accent="green" hint={`${analytics?.unsubscribed ?? 0} unsubscribed`} />
          <StatCard testid="stat-today" icon={TrendingUp} label="New today" value={analytics?.new_today ?? 0} accent="indigo" />
          <StatCard testid="stat-7d" icon={TrendingUp} label="Last 7 days" value={analytics?.new_7d ?? 0} accent="blue" />
          <StatCard testid="stat-30d" icon={TrendingUp} label="Last 30 days" value={analytics?.new_30d ?? 0} accent="blue" hint={`${analytics?.unsub_30d ?? 0} unsubscribed`} />
        </section>

        {/* Charts row */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex justify-between items-baseline mb-4">
              <h3 className="text-base font-medium">Daily growth — last 30 days</h3>
              <span className="text-xs text-gray-500">peaks scaled to fit</span>
            </div>
            <GrowthSparkline data={analytics?.growth_30d || []} />
            <div className="flex justify-between text-xs text-gray-400 mt-2">
              <span>{analytics?.growth_30d?.[0]?.date}</span>
              <span>today</span>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-base font-medium mb-4">Where they came from</h3>
            <SourceBars breakdown={analytics?.source_breakdown || []} totalActive={analytics?.active || 0} />
          </div>
        </section>

        {/* Filters + actions */}
        <section className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
                  data-testid="subscribers-search"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                data-testid="subscribers-status-filter"
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="unsubscribed">Unsubscribed</option>
              </select>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                data-testid="subscribers-source-filter"
              >
                <option value="">All sources</option>
                {sourceOptions.map(s => (
                  <option key={s} value={s}>{SOURCE_LABELS[s] || s}</option>
                ))}
              </select>
              <span className="text-sm text-gray-500">{total} match{total === 1 ? '' : 'es'}</span>
            </div>
            <button
              onClick={handleExport}
              disabled={downloading || !total}
              className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-40 text-sm"
              data-testid="subscribers-export-btn"
            >
              {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              <span>Export CSV</span>
            </button>
          </div>
        </section>

        {/* Bulk import panel */}
        {importPanelOpen ? (
          <section className="bg-white border border-gray-200 rounded-lg p-6" data-testid="import-subscribers-panel">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium flex items-center gap-2"><Upload size={18} /> Import subscribers</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Paste a list of emails (commas, spaces, semicolons or newlines).
                  Existing active subscribers are skipped; previously-unsubscribed ones are reactivated.
                </p>
              </div>
              <button
                onClick={() => { setImportPanelOpen(false); setImportResult(null); }}
                className="text-gray-400 hover:text-black"
                data-testid="close-import-panel-btn"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <label className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-2">Email list</label>
            <textarea
              value={importEmails}
              onChange={(e) => setImportEmails(e.target.value)}
              placeholder="alice@example.com, bob@example.com&#10;carol@example.com"
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
              data-testid="import-emails-textarea"
            />

            {importResult ? (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm" data-testid="import-result">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><span className="text-gray-500 text-xs">Added</span><div className="text-emerald-800 font-semibold text-lg">{importResult.added}</div></div>
                  <div><span className="text-gray-500 text-xs">Reactivated</span><div className="text-blue-800 font-semibold text-lg">{importResult.reactivated}</div></div>
                  <div><span className="text-gray-500 text-xs">Already active</span><div className="text-gray-800 font-semibold text-lg">{importResult.already_active}</div></div>
                  <div><span className="text-gray-500 text-xs">Invalid</span><div className="text-red-800 font-semibold text-lg">{importResult.invalid_count}</div></div>
                </div>
                {importResult.invalid && importResult.invalid.length > 0 ? (
                  <p className="text-xs text-red-700 mt-2 break-words">Skipped invalid: {importResult.invalid.join(', ')}</p>
                ) : null}
              </div>
            ) : null}

            <div className="mt-5 flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={() => { setImportPanelOpen(false); setImportResult(null); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-black"
                data-testid="import-cancel-btn"
              >
                Close
              </button>
              <button
                onClick={handleBulkImport}
                disabled={isImporting || !importEmails.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 text-sm"
                data-testid="import-submit-btn"
              >
                {isImporting ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                <span>{isImporting ? 'Importing…' : 'Import'}</span>
              </button>
            </div>
          </section>
        ) : null}

        {/* Bulk email panel */}
        {emailPanelOpen ? (
          <section className="bg-white border border-gray-200 rounded-lg p-6" data-testid="email-broadcast-panel">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium flex items-center gap-2"><Mail size={18} /> Send email to subscribers</h3>
                <p className="text-sm text-gray-600 mt-1">Pick a published item, choose recipients, and send a rich HTML email with a CTA button.</p>
              </div>
              <button
                onClick={() => setEmailPanelOpen(false)}
                className="text-gray-400 hover:text-black"
                data-testid="close-email-panel-btn"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-2">Content type</label>
                <div className="flex gap-2" data-testid="email-kind-tabs">
                  {[
                    { k: 'post', label: 'Post / News' },
                    { k: 'lesson', label: 'Lesson' },
                    { k: 'magnet', label: 'PDF Guide' },
                  ].map(({ k, label }) => (
                    <button
                      key={k}
                      onClick={() => setEmailKind(k)}
                      className={`px-3 py-1.5 rounded-md text-sm border ${emailKind === k ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                      data-testid={`email-kind-${k}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-2">Item</label>
                <select
                  value={selectedSlug}
                  onChange={(e) => setSelectedSlug(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                  data-testid="email-item-select"
                >
                  {(emailKind === 'post' ? emailOptions.posts :
                    emailKind === 'lesson' ? emailOptions.lessons :
                    emailOptions.magnets
                  ).map((it) => (
                    <option key={it.slug} value={it.slug}>{it.title}</option>
                  ))}
                  {((emailKind === 'post' && emailOptions.posts.length === 0) ||
                    (emailKind === 'lesson' && emailOptions.lessons.length === 0) ||
                    (emailKind === 'magnet' && emailOptions.magnets.length === 0)) ? (
                    <option value="">— nothing published yet —</option>
                  ) : null}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-2">Subject (optional override)</label>
              <input
                type="text"
                value={subjectOverride}
                onChange={(e) => setSubjectOverride(e.target.value)}
                placeholder="Leave blank to use the item's title"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                data-testid="email-subject-override"
              />
            </div>

            <div className="mt-4">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendToAll}
                  onChange={(e) => setSendToAll(e.target.checked)}
                  className="w-4 h-4 accent-black"
                  data-testid="email-send-all-toggle"
                />
                Send to all <span className="font-semibold">{analytics?.active ?? 0}</span> active subscribers
              </label>

              {!sendToAll ? (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-2">
                      Selected from list ({selectedEmails.size})
                    </label>
                    <div className="flex flex-wrap gap-1.5 min-h-[28px]" data-testid="email-selected-chips">
                      {Array.from(selectedEmails).slice(0, 20).map(e => (
                        <span key={e} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                          {e}
                          <button onClick={() => toggleEmailSelected(e)} className="text-gray-400 hover:text-red-600">
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                      {selectedEmails.size > 20 ? (
                        <span className="text-xs text-gray-500">+ {selectedEmails.size - 20} more</span>
                      ) : null}
                      {selectedEmails.size === 0 ? (
                        <span className="text-xs text-gray-400">No subscribers checked yet — use the checkboxes in the table below.</span>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-2">
                      Or paste extra emails (comma / newline separated)
                    </label>
                    <textarea
                      value={extraEmails}
                      onChange={(e) => setExtraEmails(e.target.value)}
                      placeholder="alice@example.com, bob@example.com"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      data-testid="email-extra-emails"
                    />
                    {extraEmails.trim() ? (
                      <p className="text-xs text-gray-500 mt-1">
                        {parseExtraEmails(extraEmails).length} valid email{parseExtraEmails(extraEmails).length === 1 ? '' : 's'} parsed
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="mt-5 flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                onClick={() => setEmailPanelOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-black"
                data-testid="email-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleSendBroadcast}
                disabled={isSending || !selectedSlug}
                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 text-sm"
                data-testid="email-send-btn"
              >
                {isSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                <span>{isSending ? 'Sending…' : 'Send email'}</span>
              </button>
            </div>
          </section>
        ) : null}

        {/* Table */}
        <section className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-gray-500 uppercase text-xs tracking-wider">
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      onChange={toggleAllOnPage}
                      checked={(() => {
                        const active = items.filter(s => s.active);
                        return active.length > 0 && active.every(s => selectedEmails.has(s.email));
                      })()}
                      className="w-4 h-4 accent-black"
                      data-testid="select-all-on-page"
                      title="Select all active on this page"
                    />
                  </th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Source</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Subscribed</th>
                  <th className="px-5 py-3">Unsubscribed</th>
                  <th className="px-5 py-3 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-testid="subscribers-table-body">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-5 py-12 text-center text-gray-500">
                      No subscribers match your filters yet.
                    </td>
                  </tr>
                ) : items.map((s) => {
                  const src = s.source || 'unknown';
                  const checked = selectedEmails.has(s.email);
                  return (
                    <tr key={s.email} className="hover:bg-gray-50" data-testid={`subscriber-row-${s.email}`}>
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          disabled={!s.active}
                          checked={checked}
                          onChange={() => toggleEmailSelected(s.email)}
                          className="w-4 h-4 accent-black disabled:opacity-30"
                          data-testid={`subscriber-check-${s.email}`}
                        />
                      </td>
                      <td className="px-5 py-3 font-medium text-gray-900">{s.email}</td>
                      <td className="px-5 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${SOURCE_COLORS[src] || SOURCE_COLORS.unknown}`}>
                          {SOURCE_LABELS[src] || src}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {s.active ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                            <UserCheck size={14} /> Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-gray-500 text-xs">
                            <UserX size={14} /> Unsubscribed
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-gray-600">{formatDate(s.subscribed_at)}</td>
                      <td className="px-5 py-3 text-gray-600">{formatDate(s.unsubscribed_at)}</td>
                      <td className="px-5 py-3">
                        <button
                          onClick={() => handleDelete(s.email)}
                          disabled={deletingEmail === s.email}
                          className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                          data-testid={`subscriber-delete-${s.email}`}
                          title="Delete subscriber"
                        >
                          {deletingEmail === s.email
                            ? <Loader2 size={16} className="animate-spin" />
                            : <Trash2 size={16} />}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminSubscribers;
